// Resolve Play Store / App Store URLs for an app from its bundle identifier.
//
// - Android (Play Store): the canonical URL is
//     https://play.google.com/store/apps/details?id=<package>
//   which Google Play accepts directly — built client-side, no lookup needed.
// - iOS (App Store): Apple keys its pages on a numeric trackId (id1234567890),
//   not the bundle id, so the bundle id must be translated via Apple's iTunes
//   Lookup API. That lookup now runs on the backend (GET /api/store/ios-url)
//   so the result is cached in shared Redis (7 days; 24h "not_published"
//   sentinel) and bounded/fail-soft there — instead of every browser re-paying
//   the round-trip into localStorage.
//
// Used by AppDetailsDrawer (opened from the App Deployment table) for the
// drawer's Store-listing card.

import axios from 'axios';

export interface ResolvedStoreUrl {
  /** Canonical store URL. */
  url: string;
  /** Display label for the link. */
  label: 'App Store' | 'Play Store';
  /** Apple's numeric trackId (iOS only). */
  trackId?: number;
}

/**
 * Synchronous Play Store URL builder. Android only — no lookup needed because
 * the canonical Play Store URL accepts a package name directly.
 */
export function playStoreUrlFor(bundleId: string): ResolvedStoreUrl | null {
  if (!bundleId) return null;
  return {
    url: `https://play.google.com/store/apps/details?id=${encodeURIComponent(bundleId)}`,
    label: 'Play Store',
  };
}

/**
 * Resolve iOS bundle ids to their App Store URLs via the backend lookup
 * endpoint (Redis-cached, fail-soft). Returns a map keyed by bundleId; the
 * value is the resolved URL, or `null` when the app isn't publicly listed or
 * the lookup couldn't complete. Never throws.
 */
export async function resolveIosStoreUrls(
  bundleIds: string[],
): Promise<Record<string, ResolvedStoreUrl | null>> {
  const dedup = Array.from(new Set(bundleIds.filter(Boolean)));
  const out: Record<string, ResolvedStoreUrl | null> = {};
  if (dedup.length === 0) return out;

  const token = localStorage.getItem('token');
  const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

  await Promise.all(dedup.map(async (id) => {
    try {
      const resp = await axios.get(`/api/store/ios-url?bundleId=${encodeURIComponent(id)}`, { headers });
      const url = resp.data?.url;
      out[id] = url
        ? { url, label: 'App Store', trackId: typeof resp.data?.trackId === 'number' ? resp.data.trackId : undefined }
        : null;
    } catch {
      // Fail soft — the drawer renders a graceful "unavailable" state.
      out[id] = null;
    }
  }));

  return out;
}
